import { useState } from 'react';
import { MessageCircleQuestion, X } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';

// "Contact the creator" button + form for a task — e.g. the sponsor doesn't
// understand a task and asks its author for details. Sends a reason and a
// message: recorded in the task's thread, plus an in-app notification and an
// e-mail to the creator (POST /tasks/:id/contact-creator). Renders nothing
// when the creator is unknown or is the current user.
const REASONS = [
  { value: 'precisions', key: 'tasks.contactReason.precisions' },
  { value: 'question', key: 'tasks.contactReason.question' },
  { value: 'issue', key: 'tasks.contactReason.issue' },
  { value: 'other', key: 'tasks.contactReason.other' },
] as const;

export function ContactTaskCreator({ taskId, creatorId, creatorName, currentUserId, onSent }: {
  taskId: number;
  creatorId: number | null | undefined;
  creatorName: string | null | undefined;
  currentUserId: number | undefined;
  /** Called after sending — e.g. to reload the task's thread. */
  onSent?: () => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>('precisions');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!creatorId || creatorId === currentUserId) return null;
  const name = creatorName || t('tasks.contactCreatorFallback');

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.contactTaskCreator(taskId, reason, message.trim());
      setOpen(false);
      setMessage('');
      setReason('precisions');
      alert(t('tasks.contactSent', { name }));
      onSent?.();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} title={t('tasks.contactCreatorHelp', { name })}>
        <MessageCircleQuestion size={14} /> {t('tasks.contactCreator', { name })}
      </button>
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('tasks.contactCreatorTitle', { name })}</h3>
              <button className="close-btn" onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t('tasks.contactReasonLabel')}</label>
                <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
                  {REASONS.map(r => (
                    <button key={r.value} type="button" className={`chip ${reason === r.value ? 'active' : ''}`} onClick={() => setReason(r.value)}>
                      {t(r.key)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('tasks.contactMessageLabel')}</label>
                <textarea
                  className="form-input"
                  rows={4}
                  autoFocus
                  value={message}
                  placeholder={t('tasks.contactMessagePlaceholder')}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <p className="text-sm text-ink-3" style={{ margin: 0 }}>{t('tasks.contactExplain', { name })}</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-accent" disabled={sending || !message.trim()} onClick={send}>{t('common.send')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
